import React, { useState } from 'react';
import type { LearningVocabulary } from '../../types/learning.types';
import { playWordPronunciation } from '../../utils/audio';

interface FlashcardProps {
  vocabulary: LearningVocabulary;
  isFlipped: boolean;
  onFlip: () => void;
}

export const Flashcard: React.FC<FlashcardProps> = ({
  vocabulary,
  isFlipped,
  onFlip,
}) => {
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  const handlePlayAudio = (e: React.MouseEvent) => {
    e.stopPropagation();
    playWordPronunciation(
      vocabulary.word,
      vocabulary.audioUrl,
      () => setIsPlayingAudio(true),
      () => setIsPlayingAudio(false),
    );
  };

  return (
    <div
      id="flashcard-container"
      className="perspective-1000 w-full max-w-2xl mx-auto h-[420px] cursor-pointer select-none group"
      onClick={onFlip}
    >
      <div
        id="flashcard-inner"
        className={`relative w-full h-full duration-500 transform-style-3d transition-transform ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
      >
        {/* ─── FRONT FACE ─────────────────────────────────── */}
        <div
          id="flashcard-front"
          className="absolute inset-0 w-full h-full backface-hidden rounded-2xl p-8 flex flex-col justify-between
                     bg-gradient-to-br from-surface-800/90 to-surface-900/95 border border-white/10 shadow-2xl
                     hover:border-primary-500/30 transition-colors"
        >
          {/* Top Bar: Learned Badge & Audio */}
          <div className="flex items-center justify-between">
            <span
              id="card-status-badge"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                vocabulary.learning.isLearned
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${
                  vocabulary.learning.isLearned ? 'bg-emerald-400' : 'bg-slate-400'
                }`}
              />
              {vocabulary.learning.isLearned ? 'Learned' : 'Not learned'}
            </span>

            <button
              type="button"
              id="play-audio-btn-front"
              onClick={handlePlayAudio}
              title={`Listen to pronunciation of "${vocabulary.word}"`}
              aria-label={`Listen to ${vocabulary.word}`}
              className={`p-2.5 rounded-full text-white transition-all transform active:scale-95 ${
                isPlayingAudio
                  ? 'bg-primary-500 shadow-lg shadow-primary-500/30 ring-2 ring-primary-400'
                  : 'bg-surface-700 hover:bg-primary-600 hover:text-white border border-white/10'
              }`}
            >
              <svg
                className={`w-5 h-5 ${isPlayingAudio ? 'animate-pulse' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
              </svg>
            </button>
          </div>

          {/* Center: Word & IPA */}
          <div className="text-center py-6">
            <h2
              id="flashcard-word"
              className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-3 group-hover:text-primary-300 transition-colors"
            >
              {vocabulary.word}
            </h2>
            {vocabulary.ipa && (
              <p
                id="flashcard-ipa"
                className="text-lg sm:text-xl font-mono text-primary-400/90 font-medium"
              >
                {vocabulary.ipa}
              </p>
            )}
          </div>

          {/* Bottom Bar: Hint */}
          <div className="flex items-center justify-center text-xs text-slate-500 font-medium tracking-wide">
            <span className="flex items-center gap-1.5 group-hover:text-slate-400 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Click card to reveal definition
            </span>
          </div>
        </div>

        {/* ─── BACK FACE ──────────────────────────────────── */}
        <div
          id="flashcard-back"
          className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 rounded-2xl p-8 flex flex-col justify-between
                     bg-gradient-to-br from-surface-800 to-surface-900 border border-primary-500/30 shadow-2xl overflow-y-auto"
        >
          {/* Top Bar: Word Header & Audio */}
          <div className="flex items-start justify-between pb-3 border-b border-white/10">
            <div>
              <div className="flex items-baseline gap-3">
                <h3 className="text-2xl font-bold text-white tracking-tight">
                  {vocabulary.word}
                </h3>
                {vocabulary.ipa && (
                  <span className="text-sm font-mono text-primary-400">
                    {vocabulary.ipa}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                id="play-audio-btn-back"
                onClick={handlePlayAudio}
                title={`Listen to pronunciation of "${vocabulary.word}"`}
                aria-label={`Listen to ${vocabulary.word}`}
                className={`p-2 rounded-full text-white transition-all transform active:scale-95 ${
                  isPlayingAudio
                    ? 'bg-primary-500 ring-2 ring-primary-400'
                    : 'bg-surface-700 hover:bg-primary-600 hover:text-white border border-white/10'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content Details */}
          <div className="py-4 space-y-4">
            {/* Vietnamese Meaning */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                Vietnamese Meaning
              </div>
              <p
                id="flashcard-meaning"
                className="text-xl font-semibold text-emerald-400"
              >
                {vocabulary.meaningVi}
              </p>
            </div>

            {/* Example sentence */}
            {vocabulary.exampleSentence && (
              <div className="bg-surface-900/60 rounded-xl p-3 border-l-4 border-primary-500">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">
                  Example
                </div>
                <p id="flashcard-example" className="text-sm italic text-slate-200">
                  "{vocabulary.exampleSentence}"
                </p>
              </div>
            )}

            {/* Synonyms & Collocations */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              {vocabulary.synonyms.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                    Synonyms
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {vocabulary.synonyms.map((s, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-xs text-slate-300 font-medium"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {vocabulary.collocations.length > 0 && (
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                    Collocations
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {vocabulary.collocations.map((c, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 rounded-md bg-primary-500/10 border border-primary-500/20 text-xs text-primary-300 font-medium"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bottom Bar: Hint */}
          <div className="flex items-center justify-center pt-2 border-t border-white/5 text-xs text-slate-500 font-medium tracking-wide">
            <span className="flex items-center gap-1.5 group-hover:text-slate-400 transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Click card to flip back
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
